import { defineCollection } from "@rebasepro/common";
import prospectsCollection from "./prospects.js";
import companiesCollection from "./companies.js";
import emailThreadsCollection from "./email_threads.js";
/**
 * Things a person has to do, with a date attached.
 *
 * ── Why not just read the boards ─────────────────────────────────────────────
 * The kanban boards answer "what state is everything in". They cannot express
 * "chase Dana on Thursday", because that is not a property of Dana — it is a
 * commitment made on a particular day that comes due on another one. The
 * morning brief was already computing a weak version of this every run ("N
 * leads to triage"); a task is the durable form, and the one a human can add to.
 *
 * The agent may CREATE tasks (a reply arrives, so answering it becomes due) but
 * completion is a human act, like every other gate in this app.
 *
 * Borrowed from edith-crm's `tasks`, including its default filter — an
 * always-growing list of everything ever completed is a list nobody opens.
 */
const tasksCollection = defineCollection({
    name: "Tasks",
    singularName: "Task",
    slug: "tasks",
    table: "tasks",
    description: "Follow-ups with a due date. The agent can raise one; only a person can finish one.",
    properties: {
        id: { name: "ID", type: "string", isId: "uuid", admin: { readOnly: true } },
        title: { name: "Task", type: "string", validation: { required: true } },
        prospect: {
            name: "Person",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => prospectsCollection,
                relationName: "prospect",
                localKey: "prospect_id",
                onDelete: "cascade"
            }
        },
        company: {
            name: "Company",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                relationName: "company",
                localKey: "company_id",
                onDelete: "cascade"
            }
        },
        thread: {
            name: "Conversation",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => emailThreadsCollection,
                relationName: "thread",
                localKey: "thread_id",
                onDelete: "set null"
            }
        },
        status: {
            name: "Status",
            type: "string",
            validation: { required: true },
            defaultValue: "pending",
            enum: [
                { id: "pending", label: "To do", color: "yellow" },
                { id: "done", label: "Done", color: "green" },
                { id: "dropped", label: "Dropped", color: "gray" }
            ]
        },
        priority: {
            name: "Priority",
            type: "string",
            defaultValue: "normal",
            enum: [
                { id: "high", label: "High", color: "red" },
                { id: "normal", label: "Normal", color: "blue" },
                { id: "low", label: "Low", color: "gray" }
            ]
        },
        due_date: {
            name: "Due",
            type: "date",
            description: "Overdue tasks lead the morning brief"
        },
        source: {
            name: "Raised by",
            type: "string",
            defaultValue: "agent",
            enum: [
                { id: "agent", label: "Agent", color: "blue" },
                { id: "human", label: "You", color: "green" }
            ]
        },
        /**
         * Set by the agent so it cannot raise the same task twice.
         *
         * A reply that stays unanswered for a week would otherwise collect
         * seven identical "answer Dana" tasks — one per sync — and a task list
         * that repeats itself is one nobody trusts.
         */
        dedupe_key: {
            name: "Dedupe key",
            type: "string",
            validation: { unique: true },
            admin: { hideFromCollection: true, readOnly: true }
        },
        notes: { name: "Notes", type: "string", admin: { multiline: true } },
        created_at: { name: "Created", type: "date", autoValue: "on_create", admin: { readOnly: true } },
        completed_at: { name: "Completed", type: "date" }
    },
    admin: {
        icon: "CheckSquare",
        group: "Pipeline",
        defaultViewMode: "table",
        display: {
            title: "title"
        },
        sort: ["due_date", "asc"],
        // An always-growing list of everything ever done is a list nobody opens.
        defaultFilter: { status: ["==", "pending"] },
        filterPresets: [
            { label: "To do", filterValues: { status: ["==", "pending"] } },
            { label: "High priority", filterValues: { status: ["==", "pending"], priority: ["==", "high"] } },
            { label: "Done", filterValues: { status: ["==", "done"] } }
        ],
        propertiesOrder: ["title", "status", "priority", "due_date", "prospect", "company", "thread", "source", "notes", "created_at", "completed_at"]
    }
});
export default tasksCollection;
